import React from 'react';

interface SliderNavigationProps {
  onPrev: () => void;
  onNext: () => void;
  activeIndex: number;
  totalItems: number;
  onDotClick?: (index: number) => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const SliderNavigation: React.FC<SliderNavigationProps> = ({
  onPrev,
  onNext,
  activeIndex,
  totalItems,
  onDotClick,
  prevDisabled,
  nextDisabled,
  className = '',
  style,
}) => {
  const isPrevDisabled =
    prevDisabled !== undefined ? prevDisabled : activeIndex === 0;
  const isNextDisabled =
    nextDisabled !== undefined ? nextDisabled : activeIndex === totalItems - 1;

  return (
    <div
      className={`flex items-center justify-center space-x-6 ${className}`}
      style={style}
    >
      <button
        onClick={onPrev}
        disabled={isPrevDisabled}
        aria-label="Previous"
        className={`border-gray-150 flex h-12 w-12 items-center justify-center rounded-full border bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-gray-50 disabled:opacity-40 ${isPrevDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <svg
          className="text-text-main h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5L8.25 12l7.5-7.5"
          />
        </svg>
      </button>

      {totalItems <= 8 ? (
        <>
          {/* Desktop view dots */}
          <div className="hidden items-center space-x-2.5 md:flex">
            {Array.from({ length: totalItems }).map((_, i) => (
              <button
                key={i}
                onClick={() => onDotClick?.(i)}
                disabled={!onDotClick}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-3 w-3 rounded-full transition-all duration-200 ${
                  i === activeIndex
                    ? 'bg-theme-primary scale-100'
                    : 'bg-border-default'
                }`}
              />
            ))}
          </div>
          {/* Mobile view numbers with glass effect */}
          <span className="font-mulish text-theme-primary flex min-w-14 rounded-full border border-white/35 bg-white/40 px-3.5 py-1 text-center text-sm font-bold backdrop-blur-md select-none md:hidden">
            {activeIndex + 1} / {totalItems}
          </span>
        </>
      ) : (
        <>
          {/* Desktop view numbers */}
          <span className="font-mulish text-royal-500 bg-royal-100/60 hidden min-w-14 rounded-full px-3.5 py-1 text-center text-sm font-bold select-none md:inline-block">
            {activeIndex + 1} / {totalItems}
          </span>
          {/* Mobile view numbers with glass effect */}
          <span className="font-mulish text-theme-primary flex min-w-14 rounded-full border border-white/35 bg-white/40 px-3.5 py-1 text-center text-sm font-bold backdrop-blur-md select-none md:hidden">
            {activeIndex + 1} / {totalItems}
          </span>
        </>
      )}

      <button
        onClick={onNext}
        disabled={isNextDisabled}
        aria-label="Next"
        className={`border-gray-150 flex h-12 w-12 items-center justify-center rounded-full border bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-gray-50 disabled:opacity-40 ${isNextDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <svg
          className="text-text-main h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 4.5l7.5 7.5-7.5 7.5"
          />
        </svg>
      </button>
    </div>
  );
};

export default SliderNavigation;
